import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';
import { RiLockPasswordLine } from 'react-icons/ri';
import { brandLogo } from '../../utils/brandLogos';
import { AuthContext } from '../../contexts/auth';

// ==================== ANIMATIONS ====================
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideInLeft = keyframes`
  from { opacity: 0; transform: translateX(-30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const slideInRight = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.02); }
`;

// ==================== STYLED COMPONENTS ====================
const PageContainer = styled.div`
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #318CD5 0%, #4FACFE 100%);
  padding: 20px;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(79, 172, 254, 0.08) 0%, transparent 50%);
    animation: ${pulse} 8s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    right: -30%;
    width: 80%;
    height: 80%;
    background: radial-gradient(circle, rgba(49, 140, 213, 0.1) 0%, transparent 60%);
  }
`;

const LoginCard = styled.div`
  width: 100%;
  max-width: 1000px;
  min-height: 580px;
  display: flex;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 24px;
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    flex-direction: column;
    max-width: 420px;
    min-height: auto;
  }
`;

const BrandSide = styled.div`
  width: 50%;
  background: linear-gradient(145deg, #f8fafc 0%, #e8f4fd 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  position: relative;
  overflow: hidden;
  animation: ${slideInLeft} 0.6s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background:
      radial-gradient(circle at 20% 80%, rgba(49, 140, 213, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(79, 172, 254, 0.08) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Logo = styled.img`
  width: 280px;
  height: auto;
  margin-bottom: 32px;
  animation: ${float} 4s ease-in-out infinite;
  filter: drop-shadow(0 4px 12px rgba(49, 140, 213, 0.2));
  position: relative;
  z-index: 1;
`;

const BrandTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #074D82;
  text-align: center;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
  position: relative;
  z-index: 1;
`;

const BrandSubtitle = styled.p`
  font-size: 18px;
  font-weight: 500;
  color: #318CD5;
  text-align: center;
  max-width: 300px;
  line-height: 1.5;
  position: relative;
  z-index: 1;
`;

const FormSide = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
  animation: ${slideInRight} 0.6s ease-out;

  @media (max-width: 768px) {
    width: 100%;
    padding: 40px 24px;
  }
`;

const MobileLogo = styled.img`
  display: none;
  width: 200px;
  height: auto;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 340px;
`;

const WelcomeText = styled.div`
  margin-bottom: 32px;
  animation: ${fadeIn} 0.6s ease-out 0.2s both;
`;

const WelcomeTitle = styled.h2`
  font-size: 26px;
  font-weight: 700;
  color: #1a365d;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
`;

const WelcomeSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  font-weight: 400;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  position: relative;
  animation: ${fadeIn} 0.6s ease-out ${props => 0.3 + props.$delay * 0.1}s both;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  transition: color 0.3s ease;
  z-index: 2;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 54px;
  padding: 0 48px;
  border: 2px solid #e2e8f0;
  border-radius: 14px;
  font-size: 15px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.3s ease;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: #318CD5;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(49, 140, 213, 0.1);
  }

  &:focus + ${InputIcon} {
    color: #318CD5;
  }

  &:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 1000px #f8fafc inset;
    -webkit-text-fill-color: #1e293b;
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.3s ease;
  z-index: 2;

  &:hover {
    color: #318CD5;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 54px;
  background: linear-gradient(135deg, #318CD5 0%, #4FACFE 100%);
  border: none;
  border-radius: 14px;
  color: white;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 0.6s ease-out 0.5s both;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px -5px rgba(49, 140, 213, 0.5);

    &::before {
      opacity: 1;
      animation: ${shimmer} 1.5s infinite;
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const ForgotPasswordLink = styled.div`
  text-align: center;
  margin-top: 8px;
  animation: ${fadeIn} 0.6s ease-out 0.6s both;

  span {
    color: #64748b;
    font-size: 14px;
  }

  a {
    color: #318CD5;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    margin-left: 4px;
    transition: color 0.3s ease;

    &:hover {
      color: #074D82;
      text-decoration: underline;
    }
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 24px 0;
  animation: ${fadeIn} 0.6s ease-out 0.7s both;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  span {
    color: #94a3b8;
    font-size: 13px;
    font-weight: 500;
  }
`;

const SignUpSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  animation: ${fadeIn} 0.6s ease-out 0.8s both;

  @media (max-width: 400px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const SignUpText = styled.span`
  color: #64748b;
  font-size: 14px;
`;

const SignUpButton = styled.button`
  padding: 10px 20px;
  background: transparent;
  border: 2px solid #074D82;
  border-radius: 10px;
  color: #074D82;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background: #074D82;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(7, 77, 130, 0.3);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width:1300px) {
    padding: 2px 16px;
  }

  @media (max-width: 400px) {
    padding: 2px 16px;
    font-size: 13px;
  }
`;

// ==================== COMPONENT ====================
const LoginPage = () => {
  const { t } = useTranslation('auth');
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await login(email, password);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickRecuperar = (e) => {
    e.preventDefault();
    navigate('/recuperar-senha');
  };

  const handleClickCadastro = () => {
    navigate('/informacoes');
  };

  return (
    <PageContainer>
      <LoginCard>
        <BrandSide>
          <Logo src={brandLogo} alt={t('common:logoAlt')} />
          <BrandTitle>{t('login.title')}</BrandTitle>
          <BrandSubtitle>{t('login.subtitle')}</BrandSubtitle>
        </BrandSide>

        <FormSide>
          <FormContainer>
            <MobileLogo src={brandLogo} alt={t('common:logoAlt')} />

            <WelcomeText>
              <WelcomeTitle>{t('login.welcomeBack')}</WelcomeTitle>
              <WelcomeSubtitle>{t('login.enterCredentials')}</WelcomeSubtitle>
            </WelcomeText>

            <Form onSubmit={handleSubmit}>
              <InputGroup $delay={0}>
                <StyledInput
                  type="email"
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                <InputIcon>
                  <HiOutlineMail />
                </InputIcon>
              </InputGroup>

              <InputGroup $delay={1}>
                <StyledInput
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <InputIcon>
                  <RiLockPasswordLine />
                </InputIcon>
                <PasswordToggle
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </PasswordToggle>
              </InputGroup>

              <SubmitButton type="submit" disabled={isLoading}>
                {isLoading ? '...' : t('login.loginButton')}
              </SubmitButton>

              <ForgotPasswordLink>
                <span>{t('login.forgotPassword')}</span>
                <a onClick={handleClickRecuperar}>{t('login.recover')}</a>
              </ForgotPasswordLink>
            </Form>

            <Divider>
              <span>{t('login.noAccount')}</span>
            </Divider>

            <SignUpSection>
              <SignUpButton onClick={handleClickCadastro}>
                {t('login.signUp')}
              </SignUpButton>
            </SignUpSection>
          </FormContainer>
        </FormSide>
      </LoginCard>
    </PageContainer>
  );
};

export default LoginPage;
